import { cloneElement, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { List, X } from '@phosphor-icons/react';
import Sidebar from '@/components/Sidebar';
import DashboardPage from '@/pages/Dashboard';
import InstancesPage from '@/pages/Instances';
import CampaignsListPage from '@/pages/CampaignsList';
import CampaignDetailPage from '@/pages/CampaignDetail';
import BroadcastPage from '@/pages/Broadcast';
import LinksPage from '@/pages/Links';
import LogsPage from '@/pages/Logs';
import InboxPage from '@/pages/Inbox';
import CrmPage from '@/pages/Crm';
import { InstanceProvider } from '@/context/InstanceContext';
import '@/App.css';

function Shell({ children }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('s1-theme') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('s1-theme', theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => setTheme((current) => (current === 'dark' ? 'light' : 'dark'));

  return (
    <div className="s1-app flex min-h-screen min-w-0 flex-col md:flex-row" data-theme={theme}>
      <header
        className="s1-mobile-header fixed inset-x-0 top-0 z-40 flex h-16 items-center justify-between border-b px-4 md:hidden"
        style={{ background: 'var(--s1-sidebar)', borderColor: 'var(--s1-line)' }}
      >
        <div>
          <div className="brand-script text-[25px] leading-none">Simplific</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.32em]" style={{ color: 'var(--s1-gold-text)' }}>ONE</span>
            <span className="h-3 w-px" style={{ background: 'var(--s1-divider)' }} />
            <span className="text-[10px] font-semibold" style={{ color: 'var(--s1-accent-text)' }}>Groups</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="grid h-11 w-11 place-items-center rounded-full border"
          style={{
            background: 'var(--s1-card)',
            borderColor: 'var(--s1-card-border)',
            color: 'var(--s1-accent-text)',
          }}
          aria-label="Abrir menu"
        >
          <List size={23} weight="bold" />
        </button>
      </header>

      <Sidebar theme={theme} onToggleTheme={toggleTheme} className="hidden md:flex" />

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 h-full w-full cursor-default bg-black/55"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Fechar menu"
          />
          <div className="absolute inset-y-0 left-0 flex w-[84vw] max-w-[310px] min-w-0 flex-col">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border"
              style={{
                background: 'var(--s1-card)',
                borderColor: 'var(--s1-card-border)',
                color: 'var(--s1-text)',
              }}
              aria-label="Fechar menu"
            >
              <X size={18} weight="bold" />
            </button>
            <Sidebar
              theme={theme}
              onToggleTheme={toggleTheme}
              onNavigate={() => setMobileMenuOpen(false)}
              className="h-full !w-full max-w-full overflow-y-auto border-r"
            />
          </div>
        </div>
      )}

      <main className="s1-main min-w-0 flex-1 overflow-x-hidden pt-16 md:pt-0">
        <div className="mx-auto w-full max-w-[1440px] min-w-0 px-4 py-6 sm:px-5 md:px-10 md:py-10 xl:px-14">
          {cloneElement(children, { theme, onToggleTheme: toggleTheme })}
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <InstanceProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Shell><DashboardPage /></Shell>} />
          <Route path="/instances" element={<Shell><InstancesPage /></Shell>} />
          <Route path="/campaigns" element={<Shell><CampaignsListPage /></Shell>} />
          <Route path="/campaigns/:id" element={<Shell><CampaignDetailPage /></Shell>} />
          <Route path="/broadcast" element={<Shell><BroadcastPage /></Shell>} />
          <Route path="/inbox" element={<Shell><InboxPage /></Shell>} />
          <Route path="/crm" element={<Shell><CrmPage /></Shell>} />
          <Route path="/links" element={<Shell><LinksPage /></Shell>} />
          <Route path="/logs" element={<Shell><LogsPage /></Shell>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </InstanceProvider>
  );
}

export default App;
